/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, Fragment } from "react";

// antd:
import { Form, Transfer, Divider } from "antd";
import type { TransferDirection, TransferItem } from "antd/es/transfer";
// import type { DataNode } from "antd/es/tree";
// import { PlusOutlined } from "@ant-design/icons";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import SpanE from "../react/SpanE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts"; // useAppDispatch
// Reducers:
import { getAppState, changeAppState, getLng, getDict, getUser, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

type Option = {
  key:string,
  title:string,
  disabled:boolean,
  children?:Option[]
}

type Ttransfer = {
  oneWay:boolean,
  showSearch:boolean,
  showSelectAll?:boolean,
  status?:"error" | "warning",
  width?:number,
  height?:number
};

const { rz } = window;

const makeItFlat = (itms:Array<TransferItem>) => { // Трубует доработки, если больше 2-х уровней дерева
  const flt:Array<TransferItem> = [];
  (itms).forEach((ii, i) => {
    const header = Boolean(ii?.children?.length);
    flt.push({key: ii.key || i, title: ii.title || ii.label || "", header, disabled: ii.disabled || header});
    if (header) {
      ii.children.forEach((jj, j) => {
        const jtm = {key: jj.key || (i + "-" + j), title: jj.title || jj.label || "", disabled: jj.disabled};
        flt.push(jtm)
      });
    }
  });
  return flt;
};

const RZtransfer:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZtransfer.displayName = "Transfer";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type, content, hasitems} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath]; // value, visible, disabled, loading, userStyle, userClass
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    return est;
  }, [elState?.userStyle, elState?.visible, permissions]);

  /* const contentWithIcon = useCallback(({itemkey, itemValue, itemIcon, itemIconClass, itemIconColor, itemTooltip}:{itemkey:string, itemValue:string, itemIcon?:string, itemIconClass?:string, itemIconColor?:string, itemTooltip?:string}) => {
    let ico:React.ReactElement | undefined;
    if (itemIcon) { ico = (<AppIcons icopath={itemIcon} attrs={{className: itemIconClass || "rz-margin-right-5", color: itemIconColor}} />) }
    return (
      <Fragment key={itemkey}>
        {ico}
        <SpanE key={(itemValue || "").replace((/\s+/igm), "") + "-span"} uk-tooltip={itemTooltip} >
          {itemValue}
        </SpanE>
      </Fragment>
    );
  }, []); */

  const transferType = useMemo<Ttransfer>(() => {
    const typ:Ttransfer = { oneWay: false, showSearch: false };
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["showSearch", "showsearch", "search"].indexOf(tt)) { typ.showSearch = true }
      else if (~["oneWay", "oneway"].indexOf(tt)) { typ.oneWay = true }
      else if (~["showSelectAll", "showselectall"].indexOf(tt)) { typ.showSelectAll = true }
      else if (~["error", "warning"].indexOf(tt)) { typ.status = tt }
    });

    if (attributes?.status && ~["error", "warning"].indexOf(attributes.status)) { typ.status = attributes.status }
    if (attributes?.showSearch) { typ.showSearch = (attributes.showSearch === "true") }
    if (attributes?.showSelectAll) { typ.showSelectAll = (attributes.showSelectAll === "true") }
    if (attributes?.width) { typ.width = Number(attributes.width) }
    if (attributes?.height) { typ.height = Number(attributes.height) }
    return typ;
  }, [type, attributes]);

  const listStyle = useMemo(() => {
    if (!transferType.width && !transferType.height) { return undefined }
    return {width: transferType.width, height: transferType.height};
  }, [transferType]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line max-len, react-hooks/exhaustive-deps
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const titles = useMemo<Array<string>>(() => {
    const titlesource = elState.attributes["titlesource_" + lng] || elState.attributes.titlesource || elState.attributes["titlesource_" + rlng] || (lng === "ru" ? "Источник" : "Source");
    const titletarget = elState.attributes["titletarget_" + lng] || elState.attributes.titletarget || elState.attributes["titletarget_" + rlng] || (lng === "ru" ? "Назначение" : "Target");
    return [titlesource, titletarget];
  }, [elState.attributes, lng, rlng]);

  const items = useMemo<Array<TransferItem>>(() => {
    let itms:Array<TransferItem> = [];

    const getSubContent = (cnt) => {
      if (!cnt || !rz.isArray(cnt) || !cnt.length) { return undefined }
      return cnt.map((mm) => {
        const perm1 = fnc.checkPermissions(user, {...mm?.attributes, rootAttributes: {key: rootAttributes.key}}), state1 = appState[mm?.attributes?.fullPath || mm?.fullPath];
        const key1 = (rz.isString(mm) ? mm : (mm._id || mm.key || mm.value || rz.strMakeID(16)));
        let icon1:React.ReactElement | undefined;
        if (mm?.attributes?.icon) { icon1 = (<AppIcons key={key1 + "-icon"} icopath={mm.attributes.icon} attrs={{className: mm.attributes?.iconClass || "rz-margin-right-5", color: mm.attributes?.iconColor}} />) }
        if (mm?.icon) { icon1 = (<AppIcons key={key1 + "-icon"} icopath={mm.icon} attrs={{className: mm?.iconClass || "rz-margin-right-5", color: mm?.iconColor}} />) }
        const title1 = (
          rz.isString(mm) ? mm : (mm?.attributes ? (
            mm.attributes["title_" + lng] || mm.attributes.title || mm.attributes["title_" + rlng] || mm.attributes["label_" + lng] || mm.attributes.label || mm.attributes["label_" + rlng] || ""
          ) : (
            mm["title_" + lng] || mm.title || mm["title_" + rlng] || mm["label_" + lng] || mm.label || mm["label_" + rlng] || ""
          )
          )
        );
        const ret = {
          key: key1, value: (rz.isString(mm) ? mm : (mm.value || key1)),
          title: (icon1 && !title1 ? icon1 : (!icon1 && title1 ? title1 : (
            <React.Fragment key={key1 + "-frgm"}>{icon1}({title1 ? <span key={key1 + "-ttl"}>{title1}</span> : undefined})</React.Fragment>
          ))),
          disabled: (state1?.disabled || perm1.disabled === true)
        };
        // @ts-ignore
        if (mm?.children && mm?.children?.length) { ret.children = getSubContent(mm.children) }
        return ret;
      });
    };

    if (elState?.items) {
      if (rz.isArray(elState.items)) { itms = getSubContent(elState.items) }
      else if (rz.isString(elState.items)) { itms = (elState.items.split("|")).map((value) => ({value, title: value, key: rz.strMakeID(16)})) }
    }
    else if (hasitems && content && rz.isArray(content)) { itms = getSubContent(content) }
    return itms;
  }, [elState.items, hasitems, content, user, rootAttributes.key, appState, lng, rlng]);

  const targetKeys = useMemo(() => {
    let tkeys:Array<string> = [];
    if (elState?.value) {
      if (rz.isArray(elState.value)) { tkeys = elState.value.map((vv) => (rz.isObject(vv) && (vv._id || vv.key || vv.value) ? (vv._id || vv.key || vv.value) : vv)) }
      if (rz.isString(elState.value)) { tkeys = (elState.value.split("|")).map((vv) => (vv)) }
    }
    return tkeys;
  }, [elState.value]);

  const dataSource = useMemo(() => (makeItFlat(items)), [items]);

  const onChange = useCallback((newTargetKeys:string[], direction:TransferDirection, moveKeys:string[]) => {
    if (fnc.config?.isDev) console.log({action: "<Transfer key='" + key + "'> -> onChange()", newTargetKeys, direction, moveKeys});
    dispatch(changeAppState({
      appkey: rootAttributes.key, fullPath, newState: {
        ...elState, value: dataSource.filter((ff) => (rz.arrContains(newTargetKeys, (ff._id || ff.key || ff.value))))
      }
    }));
    if (rootNamespace && (attributes.onChange || attributes.onchange) && rootNamespace[attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: newTargetKeys, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: newTargetKeys, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, dataSource, rootNamespace, attributes, user, lng, dct, safe]);

  const onSearch = useCallback((direction:string, value:string) => {
    if (fnc.config?.isDev) console.log({action: "<Transfer key='" + key + "'> -> onSearch()", direction, value});

    if (rootNamespace && (attributes.onSearch || attributes.onsearch) && rootNamespace[attributes.onSearch || attributes.onsearch]) {
      const clientFNC = rootNamespace[attributes.onSearch || attributes.onsearch];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, direction, value, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, direction, value, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, rootNamespace, attributes, user, lng, dct, safe]);

  const filterOption = useCallback((filterValue:string, item:any) => { // direction:"left" | "right"
    if (transferType.showSearch === false) { return undefined }
    let itm = (rz.isObject(item) ? (item.title || item.label || item.value || item.key) : (item || ""));
    if (rz.isObject(itm) && itm.props && itm.props.children && itm.props.children.length) { // If we have a ReactElement inside 'itm'
      const chlds = itm.props.children.filter((ch) => (ch && ch.props && ch.props.children && rz.isString(ch.props.children))).map((ch) => (ch.props.children));
      if (chlds && chlds.length) { itm = chlds[0] }
    }
    return rz.strCompare(itm, filterValue, ".caseNoSensitive.toLowerCase.trim.substring");
  }, [transferType.showSearch]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  // console.log({dataSource, targetKeys});

  return (
    wrapperForm(
      <Transfer
        key={key} style={styl} listStyle={listStyle} className={clsu.join(" ")} uk-tooltip={tooltip}
        dataSource={dataSource}
        targetKeys={targetKeys}
        titles={titles}
        onChange={onChange}
        oneWay={transferType.oneWay}
        showSearch={transferType.showSearch}
        onSearch={onSearch}
        // @ts-ignore
        filterOption={filterOption}
        showSelectAll={transferType.showSelectAll}
        status={transferType.status}
        disabled={elState?.disabled || permissions.disabled === true}
        render={(item) => {
          let ico:React.ReactElement | undefined;
          if (item.icon) { ico = (<AppIcons key={item.key + "-ico"} icopath={item.icon} attrs={{className: item.iconClass || "rz-margin-right-5", color: item.iconColor}} />) }
          if (item.header) {
            return (
              <Divider key={item.key + "-header"} className="rz-margin-top-bottom-5" orientation="left" >
                {ico}
                {item.title && (
                  <span className={item.icon ? "rz-padding-right-5" : ""}>{item.title}</span>
                )}
              </Divider>
            );
          }
          return (
            <Fragment key={item.key + "-fragment"}>
              {ico}
              <SpanE key={item.key + "-span"} className={item.class} uk-tooltip={item["tooltip_" + lng] || item.tooltip || item["tooltip_" + rlng]} >
                {item.title}
              </SpanE>
            </Fragment>
          );
        }}
      />
    )
  );
}

export default RZtransfer;
/* eslint-enable @typescript-eslint/no-unused-vars */
